import type { FC } from "react";
import { HeartIcon, ShareIcon } from "@heroicons/react/24/outline";
import { Button } from "@/components/button";
import { cn } from "@/lib/cn";
import type { CardLayoutProps } from "./layout-props";
import { PractitionerAvatar } from "./practitioner-avatar";
import { ProfessionList } from "./profession-list";
import { SkeletonBar } from "./skeleton-bar";
import { SummaryCardMeta } from "./summary-card-meta";
import { VerifiedName } from "./verified-name";

const ICON_BUTTON_CLASSES = "shrink-0 rounded-regular bg-neutral-light-grey text-neutral-black hover:bg-neutral-light-grey/80";

const MobileLayoutSkeleton: FC = () => (
  <>
    <div className="skeleton-bar-dark h-116 w-113 rounded-regular" />
    <SkeletonBar tone="dark" className="h-24 w-3/5" />
    <div className="flex w-full flex-col items-center gap-8">
      <SkeletonBar className="w-2/5" />
      <SkeletonBar className="w-1/3" />
    </div>
    <hr className="w-full border-neutral-light-grey" />
    <div className="flex w-full gap-12">
      <SkeletonBar tone="dark" className="h-48 flex-1" />
      <SkeletonBar className="size-48" />
      <SkeletonBar className="size-48" />
    </div>
  </>
);

export const MobileLayout: FC<CardLayoutProps> = ({
  content,
  onContact,
  favorited,
  onFavoriteToggle,
  onShare,
  loading,
}) => {
  if (!content || loading) return <MobileLayoutSkeleton />;

  const { name, verified, professions, location, years, photoUrl, footnote } = content;

  return (
    <>
      <PractitionerAvatar name={name} photoUrl={photoUrl} className="h-116 w-113 rounded-regular" />
      <VerifiedName name={name} verified={verified} size="s" />
      <div className="flex w-full flex-col items-center gap-8">
        <SummaryCardMeta
          profession={professions.length > 0 ? <ProfessionList entries={professions} /> : undefined}
          yearsOfExperience={years}
          location={location}
          textClassName="text-14"
        />
      </div>
      <div className="flex w-full flex-col gap-16">
        <hr className="w-full border-neutral-light-grey" />
        <div className="flex w-full flex-col gap-8">
          <div className="flex w-full items-start gap-12">
            <Button onClick={onContact} size="m" variant="primary-dark-blue" className="min-w-0 flex-1">
              Contact Me
            </Button>
            <div className="flex gap-8">
              {onFavoriteToggle && (
                <Button
                  type="button"
                  variant="icon"
                  size="m"
                  aria-label="Save practitioner"
                  aria-pressed={favorited}
                  onClick={onFavoriteToggle}
                  className={ICON_BUTTON_CLASSES}
                >
                  <HeartIcon className={cn("size-22", favorited && "fill-brand-red text-brand-red")} />
                </Button>
              )}
              {onShare && (
                <Button
                  type="button"
                  variant="icon"
                  size="m"
                  aria-label="Share this profile"
                  onClick={onShare}
                  className={ICON_BUTTON_CLASSES}
                >
                  <ShareIcon className="size-22" />
                </Button>
              )}
            </div>
          </div>
          {footnote && <p className="w-full text-12 text-center text-neutral-black/70">{footnote}</p>}
        </div>
      </div>
    </>
  );
};
